import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Search } from '../../../icons';
import { Button } from '../Button';
import { Input } from '../Input';
import { InlineNote } from '../InlineNote';
import { Modal, ModalActions, ModalFootNote } from '../Modal';
import { classifyAddressSearchError, searchAddress, type AddressSearchFailure, type JusoAddress } from '../../../api/address';
import './AddressSearchDialog.css';

/**
 * 주소 찾기 창 (2026-09-17 · 시안 address-search-draft 확정 — 사업장 상세 · 회사정보).
 *
 * ★★★ **적는 동안에는 요청하지 않는다.** 「찾기」 또는 Enter 로만 찾는다(자동완성 · 디바운스 없음).
 *     호출마다 전 회사가 함께 쓰는 한도를 태우기 때문이다.
 * ★★ 두 글자 미만은 보내기 전에 막는다(서버도 같은 기준으로 400 을 준다).
 * ★★ 고르면 `onPick(주소)` 만 부른다 — 어느 칸을 어떻게 채울지 · 창을 닫을지는 화면이 정한다.
 *     닫으면 공용 Modal(useFocusTrap)이 「주소 찾기」 단추로 포커스를 돌려준다.
 *
 * 「더 보기」는 쪽 번호 없이 다음 10건을 아래에 붙인다.
 */

const PER_PAGE = 10;
/** 서버가 받는 쪽 번호의 끝(address.routes.ts) — 그 너머는 「더 보기」를 그리지 않는다. */
const MAX_PAGE = 100;

type View =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'results'; keyword: string; total: number; page: number; items: JusoAddress[] }
  | { kind: 'error'; keyword: string; failure: Exclude<AddressSearchFailure, { kind: 'keyword' }> };

export interface AddressSearchDialogProps {
  onPick: (address: JusoAddress) => void;
  onClose: () => void;
}

export function AddressSearchDialog({ onPick, onClose }: AddressSearchDialogProps) {
  const [keyword, setKeyword] = useState('');
  const [view, setView] = useState<View>({ kind: 'idle' });
  const [tipError, setTipError] = useState<string | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  /** 늦게 온 앞 검색의 답이 새 검색을 덮지 않게 한다. */
  const seq = useRef(0);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  // ★ 검색 칸 포커스는 autoFocus 가 아니라 mount effect 로 준다. autoFocus 는 Modal 의 포커스 트랩보다 먼저 포커스를
  //   옮겨, 트랩이 「주소 찾기」 단추를 돌아갈 곳으로 기억하지 못하게 만든다(Esc 뒤 포커스가 body 로 샘 — 시험으로 잠금).
  useEffect(() => { inputRef.current?.focus(); }, []);

  const run = useCallback(async (raw: string) => {
    const kw = raw.trim();
    if ([...kw].length < 2) { setTipError('두 글자 이상 적어 주십시오.'); return; }
    const my = ++seq.current;
    setTipError(null);
    setMoreFailed(false);
    setView({ kind: 'loading' });
    try {
      const res = await searchAddress({ keyword: kw, page: 1, perPage: PER_PAGE });
      if (!alive.current || my !== seq.current) return;
      setView({ kind: 'results', keyword: kw, total: res.totalCount, page: res.page, items: res.items });
    } catch (err) {
      if (!alive.current || my !== seq.current) return;
      const failure = classifyAddressSearchError(err);
      if (failure.kind === 'keyword') {
        setTipError(failure.code === 'TOO_SHORT' ? '두 글자 이상 적어 주십시오.' : (failure.message ?? '검색어를 바꿔 다시 찾아 주십시오.'));
        setView({ kind: 'idle' });
        return;
      }
      setView({ kind: 'error', keyword: kw, failure });
    }
  }, []);

  const more = async () => {
    if (view.kind !== 'results' || moreBusy) return;
    const my = seq.current;
    setMoreBusy(true);
    setMoreFailed(false);
    try {
      const res = await searchAddress({ keyword: view.keyword, page: view.page + 1, perPage: PER_PAGE });
      if (!alive.current || my !== seq.current) return;
      setView((v) => (v.kind === 'results'
        ? { ...v, page: res.page, total: res.totalCount, items: res.items.length ? [...v.items, ...res.items] : v.items }
        : v));
    } catch {
      if (alive.current && my === seq.current) setMoreFailed(true);
    } finally {
      if (alive.current) setMoreBusy(false);
    }
  };

  const loading = view.kind === 'loading';

  const onInputKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // 화면의 form 안에 놓여도 저장이 제출되지 않게 막는다.
      e.preventDefault();
      if (!loading && !e.nativeEvent.isComposing) void run(keyword);
    } else if (e.key === 'ArrowDown' && view.kind === 'results' && view.items.length) {
      e.preventDefault();
      listRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
  };

  const onRowKey = (e: KeyboardEvent<HTMLButtonElement>, i: number) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const rows = listRef.current?.querySelectorAll<HTMLButtonElement>('button');
    if (!rows) return;
    const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
    if (next < 0) inputRef.current?.focus();
    else rows[Math.min(next, rows.length - 1)]?.focus();
  };

  let body: ReactNode;
  if (view.kind === 'idle') {
    body = (
      <div className="wc-addr__empty">
        <Search size={26} aria-hidden />
        <b>도로명 · 건물명 · 동 이름으로 찾습니다</b>
        <span>예: 세종대로 209 · 정부서울청사 · 역삼동 737</span>
      </div>
    );
  } else if (view.kind === 'loading') {
    body = (
      <div className="wc-addr__empty" role="status">
        <span className="wc-addr__spin" aria-hidden />
        <b>찾는 중…</b>
      </div>
    );
  } else if (view.kind === 'results' && view.items.length === 0) {
    body = (
      <div className="wc-addr__empty" role="status">
        <Search size={26} aria-hidden />
        <b>「{view.keyword}」에 맞는 주소가 없습니다</b>
        <span>도로명과 건물번호로 적거나(세종대로 209), 동 이름과 번지로 적어 보십시오(역삼동 737).</span>
      </div>
    );
  } else if (view.kind === 'results') {
    const left = Math.max(0, view.total - view.items.length);
    body = (
      <>
        <div className="wc-addr__count" role="status">
          <b>{view.total.toLocaleString()}</b>건 중 <b>{view.items.length.toLocaleString()}</b>건 · ↑↓ 옮기기 · Enter 고르기
        </div>
        <ul className="wc-addr__list" ref={listRef} aria-label="검색 결과">
          {view.items.map((a, i) => (
            <li key={`${a.bdMgtSn || a.roadAddr}-${i}`}>
              <button type="button" className="wc-addr__row" onClick={() => onPick(a)} onKeyDown={(e) => onRowKey(e, i)}>
                <span className="wc-addr__road">
                  {a.roadAddr}
                  {a.bdNm && <span className="wc-addr__bd"> · {a.bdNm}</span>}
                </span>
                {a.jibunAddr && <span className="wc-addr__jibun"><i>지번</i>{a.jibunAddr}</span>}
                <span className="wc-addr__zip">{a.zipNo}</span>
              </button>
            </li>
          ))}
        </ul>
        {left > 0 && view.page < MAX_PAGE && (
          <div className="wc-addr__more">
            {moreFailed && <span className="wc-addr__more-err" role="alert">더 불러오지 못했습니다 — 다시 눌러 주십시오.</span>}
            <Button variant="secondary" size="md" loading={moreBusy} onClick={() => void more()}>
              더 보기 ({left.toLocaleString()}건 남음)
            </Button>
          </div>
        )}
      </>
    );
  } else if (view.failure.kind === 'too_broad') {
    body = (
      <div className="wc-addr__note">
        <InlineNote tone="info">
          <b>「{view.keyword}」로는 너무 많이 나옵니다.</b> 도로명이나 건물 이름을 더 넣어 보십시오 — 예: 세종대로 209 · 강남파이낸스센터.
        </InlineNote>
      </div>
    );
  } else if (view.failure.kind === 'unavailable') {
    body = (
      <div className="wc-addr__note" role="alert">
        <InlineNote tone="warning">
          <b>주소 검색이 아직 연결되지 않았습니다.</b> 주소를 직접 적어 주십시오. 관리자는 관리 콘솔 › 외부 API 에서 연결합니다.
        </InlineNote>
      </div>
    );
  } else {
    body = (
      <div className="wc-addr__note" role="alert">
        <InlineNote tone="danger">
          <b>주소 검색 서비스가 지금 답하지 않습니다.</b> 다시 찾거나, 주소를 직접 적어 주십시오.
        </InlineNote>
      </div>
    );
  }

  const stuck = view.kind === 'error' && view.failure.kind !== 'too_broad';
  const footer = stuck ? (
    <ModalActions>
      {view.kind === 'error' && view.failure.kind === 'failed' && (
        <Button variant="secondary" size="md" onClick={() => void run(view.keyword)}>다시 찾기</Button>
      )}
      <Button variant="primary" size="md" onClick={onClose}>직접 적기</Button>
    </ModalActions>
  ) : (
    <>
      <ModalFootNote>Esc 로 닫기 · 닫으면 「주소 찾기」로 돌아갑니다</ModalFootNote>
      <ModalActions>
        <Button variant="secondary" size="md" onClick={onClose}>닫기</Button>
      </ModalActions>
    </>
  );

  return (
    <Modal title="주소 찾기" size="sm" accent bodyFlat onClose={onClose} footer={footer}>
      <div className="wc-addr">
        <div className="wc-addr__search">
          <Input
            ref={inputRef}
            size="lg"
            fullWidth
            prefix={<Search size={15} />}
            value={keyword}
            maxLength={80}
            aria-label="찾을 주소"
            aria-describedby="wc-addr-tip"
            invalid={tipError !== null}
            placeholder="도로명 · 건물명 · 동 이름"
            onChange={(e) => { setKeyword(e.target.value); if (tipError) setTipError(null); }}
            onKeyDown={onInputKey}
          />
          <Button variant="primary" size="lg" loading={loading} disabled={loading} onClick={() => void run(keyword)}>찾기</Button>
        </div>
        {tipError
          ? <p id="wc-addr-tip" className="wc-addr__tip wc-addr__tip--err" role="alert">{tipError}</p>
          : <p id="wc-addr-tip" className="wc-addr__tip">적는 동안에는 찾지 않습니다 — 「찾기」 또는 Enter</p>}
        <div className="wc-addr__body">{body}</div>
      </div>
    </Modal>
  );
}
